import React, { useState } from 'react';
import { Card, Btn, Input, Badge } from '../components/UI';
import { Panel, Field, FieldGrid, ProfileGridStyles, PROFILE_GRID } from '../components/profile-ui';
import Avatar from '../components/Avatar';
import * as api from '../api';
import useMyPhoto from '../hooks/useMyPhoto';

const CARD_COLORS   = ['#dbeafe','#dcfce7','#fde68a','#ede9fe','#fce7f3','#cffafe','#fed7aa','#d1fae5','#e0e7ff','#fef08a'];
const BORDER_COLORS = ['#2563eb','#16a34a','#d97706','#7c3aed','#db2777','#0891b2','#ea580c','#059669','#4338ca','#ca8a04'];

function SectionLabel({ children }) {
  return (
    <div style={{
      fontSize: 10.5, fontWeight: 700, color: 'var(--text-muted)',
      textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4,
    }}>{children}</div>
  );
}

function ReadField({ label, value, mono }) {
  return (
    <div style={{ minWidth: 0 }}>
      <SectionLabel>{label}</SectionLabel>
      <div style={{
        fontSize: 13.5, color: 'var(--text-primary)', fontWeight: 500,
        fontFamily: mono ? 'var(--mono)' : undefined, wordBreak: 'break-word',
      }}>
        {value || <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>—</span>}
      </div>
    </div>
  );
}

function PwEye({ show, toggle }) {
  return (
    <button type="button" onClick={toggle} style={{
      position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
      background: 'none', border: 'none', cursor: 'pointer', padding: 4,
      color: 'var(--text-muted)', display: 'flex', alignItems: 'center',
    }}>
      {show
        ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></svg>
        : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
      }
    </button>
  );
}

function PwField({ label, value, onChange, show, onToggle }) {
  return (
    <div>
      <SectionLabel>{label}</SectionLabel>
      <div style={{ position: 'relative' }}>
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={e => onChange(e.target.value)}
          style={{
            width: '100%', padding: '9px 36px 9px 12px', fontSize: 13,
            border: '1.5px solid var(--border-strong)', borderRadius: 8,
            background: 'var(--bg-surface)', color: 'var(--text-primary)',
            fontFamily: 'var(--font)', outline: 'none', boxSizing: 'border-box',
          }}
        />
        <PwEye show={show} toggle={onToggle} />
      </div>
    </div>
  );
}

export default function ProfilePage({ user, toast, onLogout }) {
  const myPhoto = useMyPhoto(user); // same photo as the top-right header avatar
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    phone: user?.phone || '',
  });
  const [saving, setSaving] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);
  const [pw, setPw] = useState({ current: '', next: '', confirm: '' });
  const [pwMsg, setPwMsg] = useState(null);
  const [pwSuccess, setPwSuccess] = useState(false);
  const [showPw, setShowPw] = useState({ current: false, next: false, confirm: false });

  const toggleShow = k => () => setShowPw(p => ({ ...p, [k]: !p[k] }));
  const setPwField = k => v => setPw(p => ({ ...p, [k]: v }));

  const saveProfile = async () => {
    setSaving(true);
    try {
      await api.updateMe(form);
      toast.show('Profile updated');
      setEditing(false);
    } catch (e) {
      toast.show(e.message, 'error');
    }
    setSaving(false);
  };

  const cancelEdit = () => {
    setForm({ first_name: user?.first_name || '', last_name: user?.last_name || '', phone: user?.phone || '' });
    setEditing(false);
  };

  const pwValid =
    pw.current.length > 0 &&
    pw.next.length >= 8 &&
    /[A-Z]/.test(pw.next) && /[a-z]/.test(pw.next) && /\d/.test(pw.next) &&
    pw.next !== pw.current && pw.next === pw.confirm;

  const changePassword = async () => {
    if (!pwValid) return;
    setPwSaving(true); setPwMsg(null); setPwSuccess(false);
    try {
      await api.changePassword(pw.current, pw.next);
      setPw({ current: '', next: '', confirm: '' });
      setPwSuccess(true);
      toast.show('Password changed');
      setTimeout(() => { setPwOpen(false); setPwSuccess(false); }, 1800);
    } catch (e) {
      setPwMsg(e.message || 'Could not change your password.');
    }
    setPwSaving(false);
  };

  const roles = Array.isArray(user?.roles) ? user.roles : user?.role ? [user.role] : [];
  const departments = Array.isArray(user?.departments) ? user.departments : [];
  const fullName = `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || '—';

  return (
    <div className="fade-in">
      <ProfileGridStyles />

      {/* ── Hero banner ──────────────────────────────────────────────── */}
      <div style={{
        background: 'linear-gradient(135deg, #0b318bff 0%, #1e40af 100%)',
        borderRadius: 16, padding: '28px 28px 24px', marginBottom: 20,
        display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap',
      }}>
        <Avatar first={user?.first_name} last={user?.last_name} src={myPhoto || user?.profile_pic} size={80} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 22, fontWeight: 900, color: '#fff', lineHeight: 1.2 }}>{fullName}</div>
          {user?.job_title && (
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.78)', marginTop: 3 }}>{user.job_title}</div>
          )}
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', marginTop: 4, fontFamily: 'var(--mono)' }}>
            {user?.email}
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
            <Badge color={user?.is_active ? 'green' : 'red'}>{user?.is_active ? 'Active' : 'Inactive'}</Badge>
            <Badge color={user?.is_verified ? 'green' : 'amber'}>{user?.is_verified ? 'Verified' : 'Unverified'}</Badge>
            {user?.is_locked && <Badge color="red" pulse>Locked</Badge>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <button
            onClick={onLogout}
            style={{
              padding: '8px 16px', background: 'rgba(239,68,68,0.15)',
              border: '1px solid rgba(239,68,68,0.45)', borderRadius: 9,
              color: '#fca5a5', fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
              fontFamily: 'var(--font)',
            }}>
            Sign Out
          </button>
        </div>
      </div>

      {/* ── Main grid ────────────────────────────────────────────────── */}
      <div className="user-profile-grid" style={PROFILE_GRID}>

        {/* ── Left: editable profile form ─────────────────────── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, alignSelf: 'start' }}>

          {/* Profile card — view mode / edit mode */}
          <Card style={{ padding: 0, overflow: 'hidden' }}>
            {/* Gradient strip */}
            <div style={{ height: 5, background: 'linear-gradient(90deg, #0b318bff 0%, #3b82f6 60%, #a78bfa 100%)' }} />

            {/* Avatar + identity */}
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              padding: '24px 24px 20px', borderBottom: '1px solid var(--border-light)',
              gap: 8, position: 'relative',
            }}>
              {!editing && (
                <button
                  onClick={() => setEditing(true)}
                  style={{
                    position: 'absolute', top: 14, right: 14,
                    display: 'flex', alignItems: 'center', gap: 5,
                    padding: '5px 11px', borderRadius: 7,
                    background: 'var(--accent-dim)', color: 'var(--accent)',
                    border: '1px solid color-mix(in srgb, var(--accent) 25%, transparent)',
                    fontSize: 11.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font)',
                  }}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                  </svg>
                  Edit
                </button>
              )}
              {/* Avatar with ring */}
              <div style={{
                padding: 3, borderRadius: '50%',
                background: 'linear-gradient(135deg, #0b318bff 0%, #a78bfa 100%)',
              }}>
                <div style={{ padding: 2, borderRadius: '50%', background: '#fff' }}>
                  <Avatar first={user?.first_name} last={user?.last_name} src={myPhoto || user?.profile_pic} size={68} />
                </div>
              </div>
              <div style={{ textAlign: 'center', marginTop: 2 }}>
                <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-primary)' }}>{fullName}</div>
                {user?.job_title && (
                  <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', marginTop: 2 }}>{user.job_title}</div>
                )}
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3, fontFamily: 'var(--mono)' }}>{user?.email}</div>
              </div>
            </div>

            {/* Body */}
            <div style={{ padding: '0 20px 20px' }}>
              {editing ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingTop: 20 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <Input label="First Name" value={form.first_name} onChange={v => setForm(f => ({ ...f, first_name: v }))} />
                    <Input label="Last Name" value={form.last_name} onChange={v => setForm(f => ({ ...f, last_name: v }))} />
                  </div>
                  <Input label="Phone" value={form.phone} onChange={v => setForm(f => ({ ...f, phone: v }))} placeholder="e.g. 08100000000" />
                  <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                    <Btn onClick={saveProfile} loading={saving} size="md" style={{ flex: 1 }}>Save Changes</Btn>
                    <button onClick={cancelEdit} disabled={saving} style={{
                      flex: 1, padding: '9px 12px', borderRadius: 8,
                      border: '1.5px solid var(--border-strong)', background: 'var(--bg-surface)',
                      color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600,
                      cursor: 'pointer', fontFamily: 'var(--font)',
                    }}>Cancel</button>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', paddingTop: 4 }}>
                  {[
                    { label: 'First Name', value: user?.first_name },
                    { label: 'Last Name',  value: user?.last_name },
                    { label: 'Phone',      value: user?.phone },
                  ].map(({ label, value }) => (
                    <div key={label} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '10px 0', borderBottom: '1px solid var(--border-light)',
                    }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: value ? 'var(--text-primary)' : 'var(--text-muted)' }}>{value || '—'}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>

          {/* Change Password — collapsible */}
          <Card style={{ padding: 0, overflow: 'hidden' }}>
            <button
              onClick={() => { setPwOpen(o => !o); setPwMsg(null); setPwSuccess(false); }}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '14px 18px',
                background: pwOpen ? 'linear-gradient(135deg, #0b318bff 0%, #1e40af 100%)' : 'var(--bg-surface)',
                border: 'none', cursor: 'pointer', fontFamily: 'var(--font)',
                borderBottom: pwOpen ? '1px solid rgba(255,255,255,0.12)' : 'none',
                transition: 'background 0.2s',
              }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                <span style={{
                  width: 28, height: 28, borderRadius: 8,
                  background: pwOpen ? 'rgba(255,255,255,0.15)' : 'var(--accent-dim)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={pwOpen ? '#fff' : 'var(--accent)'} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                </span>
                <span style={{
                  fontSize: 13, fontWeight: 700,
                  color: pwOpen ? '#fff' : 'var(--text-primary)',
                }}>Change Password</span>
              </div>
              <svg
                width="14" height="14" viewBox="0 0 24 24" fill="none"
                stroke={pwOpen ? '#fff' : 'var(--text-muted)'}
                strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                style={{ transform: pwOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }}>
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>

            {pwOpen && (
              <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 13 }}>
                <PwField label="Current Password" value={pw.current} onChange={setPwField('current')} show={showPw.current} onToggle={toggleShow('current')} />
                <PwField label="New Password" value={pw.next} onChange={setPwField('next')} show={showPw.next} onToggle={toggleShow('next')} />
                <PwField label="Confirm New Password" value={pw.confirm} onChange={setPwField('confirm')} show={showPw.confirm} onToggle={toggleShow('confirm')} />

                <div style={{ fontSize: 11.5, color: 'var(--text-muted)', lineHeight: 1.6 }}>
                  Min 8 characters — uppercase, lowercase, and a number.
                </div>

                {pw.confirm.length > 0 && pw.next !== pw.confirm && (
                  <div style={{ fontSize: 12, color: 'var(--red)' }}>Passwords do not match.</div>
                )}
                {pwMsg && (
                  <div style={{ fontSize: 12.5, color: 'var(--red)', background: 'var(--red-dim)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '9px 12px', lineHeight: 1.55 }}>
                    {pwMsg}
                  </div>
                )}
                {pwSuccess && (
                  <div style={{ fontSize: 12.5, color: '#16a34a', background: '#dcfce7', border: '1px solid #86efac', borderRadius: 8, padding: '9px 12px' }}>
                    Password changed successfully.
                  </div>
                )}
                <Btn onClick={changePassword} loading={pwSaving} disabled={!pwValid} size="md">
                  Change Password
                </Btn>
              </div>
            )}
          </Card>
        </div>

        {/* ── Right: read-only details ─────────────────────────── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

          <Panel title="Staff Record">
            <FieldGrid>
              <ReadField label="Employee ID" value={user?.employee_id} mono />
              <ReadField label="Job Title" value={user?.job_title} />
              <ReadField label="Grade" value={user?.grade} />
              <ReadField label="Branch" value={user?.branch} />
            </FieldGrid>
          </Panel>

          {/* Roles */}
          <Panel title={`Roles (${roles.length})`}>
            {roles.length ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
                {roles.map((r, idx) => {
                  const bg = CARD_COLORS[idx % CARD_COLORS.length];
                  const border = BORDER_COLORS[idx % BORDER_COLORS.length];
                  return (
                    <div key={r.role_id || r.id || idx} className="dk-tint-card" style={{
                      '--tint': border, background: bg, border: `1px solid ${border}`,
                      borderRadius: 10, padding: '11px 14px', minWidth: 0,
                    }}>
                      <div className="dk-tint-title" style={{ fontSize: 13, fontWeight: 800, color: '#0f172a' }}>
                        {r.display_name || r.name || r.role_name}
                      </div>
                      {(r.role_name || r.name) && r.role_name !== r.display_name && (
                        <div className="dk-tint-sub" style={{ fontSize: 10.5, color: '#475569', fontFamily: 'var(--mono)', marginTop: 2 }}>
                          {r.role_name || r.name}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>No roles assigned.</div>
            )}
          </Panel>

          {/* Departments */}
          <Panel title={`Departments (${departments.length})`}>
            {departments.length ? (
              <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
                {departments.map(d => (
                  <span key={d.id || d.name} className="dk-tint-card dk-tint-text" style={{
                    '--tint': '#a5b4fc', fontSize: 12, fontWeight: 700, color: '#4338ca',
                    background: '#e0e7ff', border: '1px solid #a5b4fc',
                    borderRadius: 7, padding: '4px 10px',
                  }}>{d.name}</span>
                ))}
              </div>
            ) : (
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>No departments assigned.</div>
            )}
          </Panel>

          {/* Account Security — read-only */}
          <Panel title="Account Security">
            <FieldGrid>
              <ReadField label="MFA" value={user?.mfa_enabled ? 'Enabled' : 'Disabled'} />
              <ReadField label="Risk Level" value={user?.risk_level || 'Low'} />
              <ReadField label="Last Sign-in" value={
                user?.last_login_at
                  ? new Date(user.last_login_at).toLocaleString(undefined, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                  : null
              } />
            </FieldGrid>
            <div style={{ marginTop: 14, fontSize: 11.5, color: 'var(--text-muted)', lineHeight: 1.6 }}>
              Locked out? Contact your administrator — they can issue a temporary password.
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}
